"use client";

import { UserPlanVo, UserProfile } from "@/api";
import { AssetsExplorerModal } from "@/component/AssetsExplorerModal";
import { SpotlightProvider } from "@/component/NewUserGuide/SpotlightContext";
import { themeStore } from "@/store/theme";
import { userStore } from "@/store/user";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import { ProgressProvider } from "@bprogress/next/app";
import { App, ConfigProvider, theme } from "antd";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

interface ProvidersProps {
  children: React.ReactNode;
  initialUserData: {
    user: UserProfile | null;
    userPlan: UserPlanVo | null;
    userCredits: number;
  } | null;
}

const Providers = observer(({ children, initialUserData }: ProvidersProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  // 在渲染期间立即同步水合数据（必须在首次渲染前完成）
  if (!isHydrated && initialUserData) {
    userStore.set("user", initialUserData.user);
    userStore.set("userPlan", initialUserData.userPlan);
    userStore.set("userCredits", initialUserData.userCredits);
    userStore.set("loading", false);
    setIsHydrated(true);
  }

  // 初始化主题
  useEffect(() => {
    themeStore.init();
  }, []);

  // 在客户端运行时读取 CSS 变量值
  const isDark = themeStore.resolvedTheme === "dark";

  const primaryColor = useMemo(() => {
    if (typeof window === "undefined") {
      // SSR 时返回默认值
      return "#5251f6";
    }
    // 读取 CSS 变量值
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue("--color-primary")
        .trim() || "#5251f6"
    );
  }, []);

  return (
    <ProgressProvider
      color="var(--color-primary)"
      options={{ showSpinner: false }}
      shallowRouting
    >
      <AntdRegistry>
        <App>
          <ConfigProvider
            wave={{ disabled: true }}
            theme={{
              algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
              token: {
                colorPrimary: primaryColor,
                fontFamily: "var(--font-alibaba-pu-hui-ti)",
              },
              components: {
                Dropdown: {
                  paddingBlock: 12,
                  controlPaddingHorizontal: 16,
                  paddingXXS: 8,
                },
                Button: {
                  borderRadius: 99,
                  borderRadiusLG: 99,
                  borderRadiusSM: 99,
                  fontSizeLG: 14,
                  borderColorDisabled: "transparent",
                  colorBgContainerDisabled: "#CECEFA",
                  colorPrimaryHover: "#4E4DEA",
                  colorPrimaryActive: "#4241C5",
                  colorTextDisabled: "#FFFFFF",
                  defaultBorderColor: "#EAECF0",
                  textHoverBg: "rgba(0, 0, 0, 0.03)",
                  primaryShadow: "none",
                  controlHeightLG: 48,
                },
                Table: {
                  borderRadius: 12,
                  headerSplitColor: "none",
                  headerBg: "var(--color-background-elevated)",
                  headerColor: "var(--color-text-primary)",
                  colorBgContainer: "var(--color-background-card)",
                  borderColor: "var(--color-background-elevated)",
                  colorText: "var(--color-text-primary)",
                },
                Tabs: {
                  itemHoverColor: "var(--color-text-primary)",
                  itemActiveColor: "var(--color-text-primary)",
                  itemSelectedColor: "var(--color-text-primary)",
                  inkBarColor: "var(--color-text-primary)",
                  itemColor: "var(--color-text-primary)",
                },
              },
            }}
          >
            <SpotlightProvider>
              {children}
              <AssetsExplorerModal />
            </SpotlightProvider>
          </ConfigProvider>
        </App>
      </AntdRegistry>
    </ProgressProvider>
  );
});

export default Providers;
